import React, { useState, useEffect } from 'react';
import './OrderTracker.css';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const OrderTracker = ({ orderId, phone, onBack }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestingDelivery, setRequestingDelivery] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (orderId) {
      fetchOrder();
      // Poll for updates every 10 seconds
      const interval = setInterval(fetchOrder, 10000);
      return () => clearInterval(interval);
    }
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/orders/${orderId}`);
      setOrder(response.data.order);
      setError(null);
    } catch (error) {
      console.error('Error fetching order:', error);
      setError('Unable to load order details');
    } finally {
      setLoading(false);
    }
  };

  const requestDelivery = async () => {
    setRequestingDelivery(true);
    try {
      // This would trigger the on-demand delivery request
      // For now, just update status to show it's been requested
      alert('Delivery requested! Your package will be on its way within 45 minutes.');
      // In production, this would call an API endpoint to request delivery
    } catch (error) {
      alert('Error requesting delivery. Please try again.');
    } finally {
      setRequestingDelivery(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#FF9500',
      processing: '#007AFF',
      'in-transit': '#5856D6',
      'out-for-delivery': '#AF52DE',
      delivered: '#34C759',
      'at-warehouse': '#FF9500'
    };
    return colors[status] || '#86868b';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Order Pending',
      processing: 'Processing',
      'in-transit': 'In Transit to Warehouse',
      'at-warehouse': 'Ready for Delivery',
      'out-for-delivery': 'Out for Delivery',
      delivered: 'Delivered'
    };
    return labels[status] || status;
  };

  const canRequestDelivery = order?.status === 'at-warehouse' || order?.status === 'processing';

  if (loading) {
    return (
      <div className="tracker-container">
        <div className="tracker-loading">
          <div className="spinner"></div>
          <p>Loading your order...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="tracker-container">
        <div className="tracker-error">
          <p>{error || 'Order not found'}</p>
          {onBack && (
            <button className="btn-primary" onClick={onBack}>
              Back to Checkout
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="tracker-container">
      <div className="tracker-header">
        {onBack && (
          <button className="back-btn" onClick={onBack}>
            ← Back
          </button>
        )}
        <h1 className="tracker-title">Track Your Order</h1>
      </div>

      <div className="tracker-content">
        {/* Status Card */}
        <div className="status-card">
          <div className="status-indicator" style={{ backgroundColor: getStatusColor(order.status) }}>
            <div className="status-circle"></div>
          </div>
          <div className="status-info">
            <h2 className="status-label">{getStatusLabel(order.status)}</h2>
            <p className="status-description">
              {order.status === 'pending' && 'Your order is being prepared by the store.'}
              {order.status === 'processing' && 'Your order is being processed and will arrive at our warehouse soon.'}
              {order.status === 'in-transit' && 'Your package is on its way to our warehouse.'}
              {order.status === 'at-warehouse' && 'Your package has arrived at our warehouse! You can now request delivery.'}
              {order.status === 'out-for-delivery' && 'Our driver is bringing your package to your door.'}
              {order.status === 'delivered' && 'Your package has been delivered successfully!'}
            </p>
          </div>
        </div>

        {/* Request Delivery Button */}
        {canRequestDelivery && (
          <div className="delivery-request-card">
            <div className="delivery-request-info">
              <h3>Ready for Delivery?</h3>
              <p>Request on-demand delivery to your door in 45 minutes</p>
            </div>
            <button 
              className="request-delivery-btn"
              onClick={requestDelivery}
              disabled={requestingDelivery}
            >
              {requestingDelivery ? 'Requesting...' : 'Request Delivery Now'}
            </button>
          </div>
        )}

        {/* Order Details */}
        <div className="order-details-card">
          <h3 className="card-title">Order Details</h3>
          <div className="detail-row">
            <span className="detail-label">Store</span>
            <span className="detail-value">{order.storeName}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Items</span>
            <span className="detail-value">{order.items?.length || 0} item(s)</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Delivery Address</span>
            <span className="detail-value">{order.address}</span>
          </div>
          {order.deliveryMethod === 'on-demand' && (
            <div className="detail-row">
              <span className="detail-label">Delivery Method</span>
              <span className="detail-value delivery-method-badge">
                🚴 On-Demand - Homie
              </span>
            </div>
          )}
          {order.estimatedDelivery && (
            <div className="detail-row">
              <span className="detail-label">Estimated Delivery</span>
              <span className="detail-value">
                {new Date(order.estimatedDelivery).toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="timeline-card">
          <h3 className="card-title">Order Timeline</h3>
          <div className="timeline">
            <div className={`timeline-item ${order.status !== 'pending' ? 'completed' : ''}`}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>Order Placed</h4>
                <p>{new Date(order.createdAt).toLocaleString()}</p>
              </div>
            </div>
            <div className={`timeline-item ${['processing', 'in-transit', 'at-warehouse', 'out-for-delivery', 'delivered'].includes(order.status) ? 'completed' : ''}`}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>Processing</h4>
                <p>Preparing your order</p>
              </div>
            </div>
            <div className={`timeline-item ${['in-transit', 'at-warehouse', 'out-for-delivery', 'delivered'].includes(order.status) ? 'completed' : ''}`}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>In Transit to Warehouse</h4>
                <p>On the way to our warehouse</p>
              </div>
            </div>
            <div className={`timeline-item ${['at-warehouse', 'out-for-delivery', 'delivered'].includes(order.status) ? 'completed' : ''}`}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>At Warehouse</h4>
                <p>Ready for delivery request</p>
              </div>
            </div>
            {order.deliveryMethod === 'on-demand' && (
              <div className={`timeline-item ${['out-for-delivery', 'delivered'].includes(order.status) ? 'completed' : ''}`}>
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <h4>Delivery Requested</h4>
                  <p>You requested delivery</p>
                </div>
              </div>
            )}
            <div className={`timeline-item ${['out-for-delivery', 'delivered'].includes(order.status) ? 'completed' : ''}`}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>Out for Delivery</h4>
                <p>Driver is on the way</p>
              </div>
            </div>
            <div className={`timeline-item ${order.status === 'delivered' ? 'completed' : ''}`}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>Delivered</h4>
                {order.deliveredAt && (
                  <p>{new Date(order.deliveredAt).toLocaleString()}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="support-card">
          <h3 className="card-title">Need Help?</h3>
          <p>If you have any questions about your order, our support team is here to help.</p>
          <button className="btn-secondary">
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderTracker;
